import { useCallback, useEffect, useState } from 'react';
import {
  operationalRealtimeClient,
  CollabMessage,
  RealtimeProjectionMessage
} from '../realtime/operationalRealtimeClient';
import {
  isCollabConflictUxEnabled,
  isCollabPresenceEnabled,
  isCollabRealtimeEnabled,
  isCollabTimelineEnabled
} from '../collab/collabFeatureFlags';
import { explainsConflict } from './conflictUxMessages';

const HEARTBEAT_MS = Number(process.env.REACT_APP_COLLAB_SESSION_HEARTBEAT_MS || 25_000);

export type TimelineEntryLite = {
  id: string;
  summary: string;
  eventType: string;
  timestamp: string;
};

export type RoutePresenceLite = {
  editors: Array<{ displayName: string; userId: number; role: string }>;
  viewers: Array<{ displayName: string; userId: number; role: string }>;
  softWarnings: string[];
};

export type UseRouteCollaborationResult = {
  presence: RoutePresenceLite | null;
  softWarningsTop: string[];
  staleRemoteBanner: boolean;
  financeStaleHint: boolean;
  conflictExplanation: ReturnType<typeof explainsConflict>;
  timelineEntries: TimelineEntryLite[];
  clearConflict: () => void;
  clearStaleBanner: () => void;
};

export function useRouteCollaboration(routeId: number | undefined): UseRouteCollaborationResult {
  const [presence, setPresence] = useState<RoutePresenceLite | null>(null);
  const [softWarningsTop, setSoftWarningsTop] = useState<string[]>([]);
  const [staleRemoteBanner, setStaleRemoteBanner] = useState(false);
  const [financeStaleHint, setFinanceStaleHint] = useState(false);
  const [conflictEventType, setConflictEventType] = useState<string | null>(null);
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntryLite[]>([]);
  const conflictExplanation =
    conflictEventType && isCollabConflictUxEnabled() ? explainsConflict(conflictEventType) : null;

  const applyRoutePresencePayload = useCallback((payload: Record<string, unknown>) => {
    const editors = Array.isArray(payload.editors)
      ? (payload.editors as Array<{ displayName: string; userId: number; role: string }>)
      : [];
    const viewers = Array.isArray(payload.viewers)
      ? (payload.viewers as Array<{ displayName: string; userId: number; role: string }>)
      : [];
    const softWarnings = Array.isArray(payload.softWarnings)
      ? (payload.softWarnings as string[])
      : [];
    setPresence({ editors, viewers, softWarnings });
    setSoftWarningsTop(softWarnings);
  }, []);

  useEffect(() => {
    const rid = routeId ?? 0;
    if (
      rid <= 0 ||
      !isCollabRealtimeEnabled() ||
      !operationalRealtimeClient.isConnected() ||
      !isCollabPresenceEnabled()
    )
      return;

    operationalRealtimeClient.subscribeRoute(rid);
    operationalRealtimeClient.collabRouteSessionStart(rid, 'edit');

    const hb = window.setInterval(() => {
      if (operationalRealtimeClient.isConnected()) {
        operationalRealtimeClient.collabRouteSessionPing(rid);
      }
    }, HEARTBEAT_MS);

    const unsubCollab = operationalRealtimeClient.onCollab((msg: CollabMessage) => {
      if (msg.kind !== 'route_presence') return;
      const p = Number((msg.payload as { routeId?: number }).routeId);
      if (p !== rid) return;
      applyRoutePresencePayload(msg.payload);
    });

    const unsubProj = operationalRealtimeClient.onProjection((msg: RealtimeProjectionMessage) => {
      const ch = msg.channel ?? '';
      if (!ch.startsWith('route:')) return;
      const pid = Number(ch.split(':')[1]);
      if (pid !== rid) return;

      const evt = msg.event?.eventType ?? msg.projection?.payload?.eventType;
      if (
        evt &&
        ['ROUTE_VERSION_CONFLICT', 'STOP_PATCH_GRAPH_STALE', 'STOP_PATCH_EXECUTED_REORDER_FORBIDDEN'].includes(
          evt
        ) &&
        isCollabConflictUxEnabled()
      ) {
        setConflictEventType(evt);
      }

      if (msg.projection?.kind === 'route_detail' && evt && !evt.includes('CONFLICT')) {
        setStaleRemoteBanner(true);
      }

      if (
        (msg.projection?.payload?.invalidateTags ?? []).includes('Finance') ||
        evt === 'ROUTE_FINANCE_CHANGED' ||
        evt === 'PAYMENT_RECEIVED'
      ) {
        setFinanceStaleHint(true);
      }

      const te = msg.projection?.payload?.timelineEntry;
      if (te && typeof te === 'object' && isCollabTimelineEnabled()) {
        const mutationId = String((te as { mutationId?: string }).mutationId ?? '');
        const summary = String((te as { summary?: string }).summary ?? '');
        const eventType = String((te as { eventType?: string }).eventType ?? 'ROUTE_MUTATED');
        const timestamp = String((te as { timestamp?: string }).timestamp ?? new Date().toISOString());
        if (summary) {
          setTimelineEntries((prev) => [
            {
              id: `${mutationId}:${timestamp}:${summary.slice(0, 24)}`,
              summary,
              eventType,
              timestamp
            },
            ...prev.filter((row) => row.id !== `${mutationId}:${timestamp}:${summary.slice(0, 24)}`)
          ].slice(0, 50));
        }
      }
    });

    setStaleRemoteBanner(false);
    setConflictEventType(null);

    return () => {
      clearInterval(hb);
      unsubCollab();
      unsubProj();
      if (rid > 0 && operationalRealtimeClient.isConnected()) {
        operationalRealtimeClient.collabRouteSessionEnd(rid);
        operationalRealtimeClient.unsubscribeRoute(rid);
      }
      setPresence(null);
      setSoftWarningsTop([]);
    };
  }, [routeId, applyRoutePresencePayload]);

  return {
    presence,
    softWarningsTop,
    staleRemoteBanner,
    financeStaleHint,
    conflictExplanation,
    timelineEntries,
    clearConflict: () => setConflictEventType(null),
    clearStaleBanner: () => setStaleRemoteBanner(false)
  };
}
